import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ADSENSE_CLIENT as CLIENT, AD_SLOTS } from "@/config/ads";
import { DirectBanner, useBanners } from "@/components/DirectBanner";

const FALLBACK = { leaderboard: "default-leaderboard", hero: "default-leaderboard", sidebar: "default-sidebar", skyscraper: "default-sidebar", infeed: "infeed" };
const VERTICAL = new Set(["sidebar", "skyscraper"]);

const SIZES = {
  leaderboard: "min-h-[60px] md:min-h-[90px]",
  hero: "min-h-[120px] md:min-h-[250px]",
  sidebar: "min-h-[250px]",
  skyscraper: "min-h-[250px] lg:min-h-[600px]",
  infeed: "min-h-[120px]",
};

const useNarrow = () => {
  const [narrow, setNarrow] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
};

function usePick(variant) {
  const banners = useBanners();
  const narrow = useNarrow();
  const seed = useRef(Math.random());
  return useMemo(() => {
    const kind = VERTICAL.has(variant) ? "vertical" : narrow && banners.some((b) => b.kind === "mobile") ? "mobile" : "horizontal";
    const pool = banners.filter((b) => b.kind === kind);
    return pool.length ? pool[Math.floor(seed.current * pool.length)] : null;
  }, [banners, narrow, variant]);
}

const Reserved = ({ id, variant }) => (
  <div className={`${SIZES[variant]} flex flex-col items-center justify-center gap-1 rounded-md border border-dashed border-slate-700/80 bg-[#0F141E] px-4 text-center`}>
    <span className="text-xs text-slate-500">Ad space reserved</span>
    <Link to="/advertise" data-testid={`ad-slot-${id}-advertise-link`} className="text-xs text-sky-400/80 hover:text-sky-300 transition-colors">Advertise here</Link>
  </div>
);

export const AdSlot = ({ id, variant = "leaderboard", slot, className = "" }) => {
  const ref = useRef(false);
  const banner = usePick(variant);
  const slotId = slot || AD_SLOTS[id] || AD_SLOTS[FALLBACK[variant]] || "";
  const adsense = !banner && CLIENT && slotId;

  useEffect(() => {
    if (adsense && !ref.current) {
      ref.current = true;
      try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch { /* ad blocked */ }
    }
  }, [adsense]);

  return (
    <aside data-testid={`ad-slot-${id}`} aria-label="Advertisement" className={`w-full ${className}`}>
      <p className={`font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5 ${banner ? "text-center" : ""}`}>Advertisement</p>
      {banner ? <DirectBanner banner={banner} id={id} /> : adsense ? (
        <ins className="adsbygoogle block" style={{ display: "block" }} data-ad-client={CLIENT}
          data-ad-slot={slotId} data-ad-format="auto" data-full-width-responsive="true" />
      ) : <Reserved id={id} variant={variant} />}
    </aside>
  );
};
